import React from "react";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { resolveUploadedMediaUrl } from "../../config/api";
import {
    ADMISSION_STATUSES,
    GENDERS,
    GUARDIAN_RELATIONS,
    MALAWI_DISTRICTS,
    SPONSOR_TYPES,
    YEAR_OF_STUDY_OPTIONS,
    getExamRegistrationField,
} from "./studentEnrollmentConstants";
import HostelRoomPickerFields from "./HostelRoomPickerFields";
import { findHostelIdForRoom } from "../../utils/hostelEnrollmentOptions";

/**
 * Shared student profile fields (director add + form-teacher enroll).
 */
export default function StudentEnrollmentFormFields({
    formContext,
    variant = "admin",
    situation = "Student",
    classOptions = [],
    homeroomTerm = null,
}) {
    const {
        values,
        setField,
        selectedClass,
        isUniversity,
        showGuardianSection,
        showResidenceSection,
        canPickResidence,
        boardingMode,
        hostels,
        photoFile,
        previewUrl,
        fileInputRef,
        handlePhotoPick,
        clearPhotoPick,
    } = formContext;

    const isFormTeacher = variant === "formTeacher";
    const examRegistrationField = getExamRegistrationField(selectedClass);

    return (
        <>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                Identity & access
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        required
                        label="Full name"
                        value={values.name}
                        onChange={(e) => setField("name", e.target.value)}
                        autoComplete="name"
                        placeholder="Student legal name"
                    />
                </Grid>
                {situation === "Student" && !isFormTeacher && classOptions.length > 0 && (
                    <Grid item xs={12}>
                        <FormControl fullWidth required>
                            <InputLabel id="enroll-class-label">Class</InputLabel>
                            <Select
                                labelId="enroll-class-label"
                                label="Class"
                                value={values.sclassName}
                                onChange={(e) => setField("sclassName", e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>Select class</em>
                                </MenuItem>
                                {classOptions.map((classItem) => {
                                    const id = classItem._id || classItem.id;
                                    return (
                                        <MenuItem key={id} value={id}>
                                            {classItem.sclassName}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                )}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Student ID"
                        value={
                            values.rollNum !== "" && values.rollNum != null ? values.rollNum : "—"
                        }
                        InputProps={{ readOnly: true }}
                        helperText={
                            values.sclassName
                                ? "Assigned automatically — next number in this class"
                                : "Select a class first"
                        }
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        required
                        type="password"
                        label="Login password"
                        value={values.password}
                        onChange={(e) => setField("password", e.target.value)}
                        autoComplete="new-password"
                        helperText="At least 8 characters — student uses this to sign in"
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                Profile
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp,image/*"
                        hidden
                        onChange={handlePhotoPick}
                    />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                        <Avatar
                            src={previewUrl || resolveUploadedMediaUrl(values.photoUrl) || undefined}
                            alt="Preview"
                            sx={{ width: 96, height: 96, fontSize: "2rem", fontWeight: 700 }}
                        >
                            {(values.name || "?").charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    startIcon={<PhotoCameraIcon />}
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{ borderRadius: 2 }}
                                >
                                    Choose or take photo
                                </Button>
                                {(photoFile || previewUrl) && (
                                    <Button
                                        type="button"
                                        variant="text"
                                        color="error"
                                        onClick={clearPhotoPick}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        Remove photo
                                    </Button>
                                )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                JPEG, PNG, GIF, or WebP — max 5 MB.
                            </Typography>
                        </Box>
                    </Stack>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Photo URL (optional)"
                        value={values.photoUrl}
                        onChange={(e) => setField("photoUrl", e.target.value)}
                        placeholder="https://…"
                        disabled={Boolean(photoFile)}
                        helperText={
                            photoFile
                                ? "Remove the uploaded photo to use a link instead."
                                : "Alternatively paste a hosted image URL."
                        }
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <InputLabel id="gender-label">Gender</InputLabel>
                        <Select
                            labelId="gender-label"
                            label="Gender"
                            value={values.gender}
                            onChange={(e) => setField("gender", e.target.value)}
                        >
                            <MenuItem value="">
                                <em>Not specified</em>
                            </MenuItem>
                            {GENDERS.map((g) => (
                                <MenuItem key={g} value={g}>
                                    {g}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Date of birth"
                        type="date"
                        value={values.dateOfBirth}
                        onChange={(e) => setField("dateOfBirth", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Nationality"
                        value={values.nationality}
                        onChange={(e) => setField("nationality", e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <InputLabel id="district-label">District</InputLabel>
                        <Select
                            labelId="district-label"
                            label="District"
                            value={values.district}
                            onChange={(e) => setField("district", e.target.value)}
                        >
                            <MenuItem value="">
                                <em>Not specified</em>
                            </MenuItem>
                            {MALAWI_DISTRICTS.map((item) => (
                                <MenuItem key={item} value={item}>
                                    {item}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {showGuardianSection && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                        Parent / guardian
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Guardian name"
                                value={values.guardianName}
                                onChange={(e) => setField("guardianName", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel id="guardian-relation-label">Relationship to student</InputLabel>
                                <Select
                                    labelId="guardian-relation-label"
                                    label="Relationship to student"
                                    value={values.guardianRelation}
                                    onChange={(e) => setField("guardianRelation", e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Not specified</em>
                                    </MenuItem>
                                    {GUARDIAN_RELATIONS.map((rel) => (
                                        <MenuItem key={rel} value={rel}>
                                            {rel}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Guardian phone"
                                value={values.guardianPhone}
                                onChange={(e) => setField("guardianPhone", e.target.value)}
                                type="tel"
                                helperText="Primary SMS contact for attendance, fees, results, and notices"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Secondary guardian phone"
                                value={values.secondaryGuardianPhone}
                                onChange={(e) => setField("secondaryGuardianPhone", e.target.value)}
                                type="tel"
                                helperText="Optional — also receives school SMS alerts"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Student phone"
                                value={values.studentPhone}
                                onChange={(e) => setField("studentPhone", e.target.value)}
                                type="tel"
                                helperText="Optional — SMS when also-notify-student is enabled"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Guardian email"
                                type="email"
                                value={values.guardianEmail}
                                onChange={(e) => setField("guardianEmail", e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </>
            )}

            {showGuardianSection && examRegistrationField && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                        Exam registration
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={examRegistrationField.label}
                                value={values[examRegistrationField.key]}
                                onChange={(e) => setField(examRegistrationField.key, e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </>
            )}

            {isUniversity && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                        University registration
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label="Student registration number"
                                value={values.studentRegNumber}
                                onChange={(e) => setField("studentRegNumber", e.target.value)}
                                helperText="e.g. UNIMA/CS/2025/001"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Programme"
                                value={values.programme}
                                onChange={(e) => setField("programme", e.target.value)}
                                placeholder="e.g. BSc Computer Science"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Faculty"
                                value={values.faculty}
                                onChange={(e) => setField("faculty", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Department"
                                value={values.department}
                                onChange={(e) => setField("department", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel id="year-of-study-label">Year of study</InputLabel>
                                <Select
                                    labelId="year-of-study-label"
                                    label="Year of study"
                                    value={values.yearOfStudy}
                                    onChange={(e) => setField("yearOfStudy", e.target.value)}
                                >
                                    {YEAR_OF_STUDY_OPTIONS.map((year) => (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel id="sponsor-type-label">Sponsor type</InputLabel>
                                <Select
                                    labelId="sponsor-type-label"
                                    label="Sponsor type"
                                    value={values.sponsorType}
                                    onChange={(e) => setField("sponsorType", e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Not specified</em>
                                    </MenuItem>
                                    {SPONSOR_TYPES.map((item) => (
                                        <MenuItem key={item} value={item}>
                                            {item}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                        Emergency contact
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Next of kin name"
                                value={values.nextOfKinName}
                                onChange={(e) => setField("nextOfKinName", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Next of kin phone"
                                value={values.nextOfKinPhone}
                                onChange={(e) => setField("nextOfKinPhone", e.target.value)}
                                type="tel"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Relationship"
                                value={values.nextOfKinRelation}
                                onChange={(e) => setField("nextOfKinRelation", e.target.value)}
                                placeholder="e.g. Mother, Father, Sibling"
                            />
                        </Grid>
                    </Grid>
                </>
            )}

            {showResidenceSection && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                        Residence & boarding
                    </Typography>
                    <Grid container spacing={2}>
                        {canPickResidence ? (
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <InputLabel id="residence-type-label">Residence type</InputLabel>
                                    <Select
                                        labelId="residence-type-label"
                                        label="Residence type"
                                        value={values.residenceType}
                                        onChange={(e) => {
                                            setField("residenceType", e.target.value);
                                            if (e.target.value !== "Boarding") {
                                                setField("hostelId", "");
                                                setField("hostelRoomId", "");
                                            }
                                        }}
                                    >
                                        <MenuItem value="Day">Day scholar</MenuItem>
                                        <MenuItem value="Boarding">Boarder</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        ) : (
                            <Grid item xs={12}>
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    This school is <strong>boarding only</strong> — all students are registered as
                                    boarders.
                                </Alert>
                            </Grid>
                        )}
                        {(values.residenceType === "Boarding" ||
                            boardingMode === "BoardingOnly") && (
                            <HostelRoomPickerFields
                                hostels={hostels}
                                studentGender={values.gender}
                                hostelId={
                                    values.hostelId ||
                                    findHostelIdForRoom(hostels, values.hostelRoomId)
                                }
                                hostelRoomId={values.hostelRoomId}
                                bedLabel={values.bedLabel}
                                boardingMode={boardingMode}
                                roomRequired={
                                    boardingMode === "BoardingOnly" ||
                                    values.residenceType === "Boarding"
                                }
                                onHostelChange={(id) => {
                                    setField("hostelId", id);
                                    setField("hostelRoomId", "");
                                }}
                                onRoomChange={(roomId) => {
                                    setField("hostelRoomId", roomId);
                                    if (roomId) {
                                        setField(
                                            "hostelId",
                                            findHostelIdForRoom(hostels, roomId),
                                        );
                                    }
                                }}
                                onBedLabelChange={(v) => setField("bedLabel", v)}
                            />
                        )}
                    </Grid>
                </>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                Admission
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Admission date"
                        type="date"
                        value={values.admissionDate}
                        onChange={(e) => setField("admissionDate", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                {!isFormTeacher && (
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="admission-status-label">Admission status</InputLabel>
                            <Select
                                labelId="admission-status-label"
                                label="Admission status"
                                value={values.admissionStatus}
                                onChange={(e) => setField("admissionStatus", e.target.value)}
                            >
                                {ADMISSION_STATUSES.map((item) => (
                                    <MenuItem key={item} value={item}>
                                        {item}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                )}
                <Grid item xs={12}>
                    {isFormTeacher ? (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            This profile is sent to the director as <strong>pending</strong>. School fees are assigned
                            only after verification under <strong>Verify enrollments</strong>.
                            {homeroomTerm ? ` (${homeroomTerm.theRole} enrollment)` : ""}
                        </Alert>
                    ) : (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            Fees are assigned automatically from the active <strong>fee structure</strong> for the
                            selected class after the student is created.
                        </Alert>
                    )}
                </Grid>
            </Grid>
        </>
    );
}
